import type { CommandKind, CommandPayloads } from '@didto/domain';
import { newCommandId } from '@didto/domain';
import { Button, Field, Input, Select, Sheet, Surface } from '@didto/ui';
import { useQueryClient } from '@tanstack/react-query';
import type { FormEvent, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { runCommand } from '@/lib/api';
import type { CommandResult } from '@/lib/api';
import { errorText, isNetworkError, toAppError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { REVIEW_INVALIDATE, tokenIsFresh } from '@/lib/review';
import { supabase } from '@/lib/supabase';
import '@/routes/shifts/shifts.css';
import { Loading, NoAccess } from './guards';
import { useIdleLock } from './idle';
import { useSession } from './session';
import { useSetting } from './useSetting';
import { useToast } from './toast';

/**
 * The owner's financial-review pages (ADR-026). Signed-in account only, with shift.review at the
 * location; never a tablet PIN session, and MANAGER holds no shift.review. Actions that clear an
 * item or mark something reviewed are sensitive: the server takes them only within
 * security.owner_reauth_seconds of the owner's last authentication (the token's `amr` claim), so the
 * gate asks for the authenticator code (or the password, without an authenticator) and retries the
 * same command. The pages lock after the same number of seconds without activity.
 */
interface OwnerReviewContext {
  locationId: string;
  setLocationId: (id: string) => void;
  /** Runs a command for the owner. Returns null when it was refused or cancelled (already toasted). */
  run: <K extends CommandKind>(
    kind: K,
    payload: CommandPayloads[K],
  ) => Promise<CommandResult | null>;
  /** Asks for a fresh authentication unless the token is recent enough. False when cancelled. */
  ensureFresh: () => Promise<boolean>;
}

const Ctx = createContext<OwnerReviewContext | null>(null);

export function useOwnerReview(): OwnerReviewContext {
  const v = useContext(Ctx);
  if (!v) throw new Error('useOwnerReview outside OwnerReviewGate');
  return v;
}

const LOCATION_KEY = 'didto-review-location';

/**
 * The owner's pages: the signed-in owner (never a tablet), with the authenticator when one is enrolled, holding an
 * owner-only permission: shift.review for the review, report.money for the money reports (ADR-043).
 */
const REFUSALS = {
  'shift.review': {
    device: "Shift review is the owner's, from their signed-in account, never on a tablet.",
    role: "Shift review is the owner's. Your role does not include it.",
  },
  'report.money': {
    device: 'Money reports are the owner’s, from their signed-in account, never on a tablet.',
    role: 'Money reports are the owner’s. Your role does not include them.',
  },
} as const;

export function OwnerReviewGate({
  children,
  permission = 'shift.review',
}: {
  children: ReactNode;
  permission?: keyof typeof REFUSALS;
}) {
  const s = useSession();
  const loc = useLocation();
  if (!s.ready) return <Loading />;
  if (!s.authed) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (s.me?.principal === 'device') return <NoAccess reason={REFUSALS[permission].device} />;
  if (s.me?.principal !== 'human')
    return (
      <NoAccess reason="This login is not linked to an employee. Ask the owner to link it under Employees." />
    );
  if (s.me.mfa_enrolled && s.me.aal !== 'aal2') return <Navigate to="/mfa" replace />;
  if (!s.can(permission)) return <NoAccess reason={REFUSALS[permission].role} />;
  return <OwnerReviewProvider>{children}</OwnerReviewProvider>;
}

function OwnerReviewProvider({ children }: { children: ReactNode }) {
  const s = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { toast } = useToast();
  const reauthSeconds = Number(useSetting('security.owner_reauth_seconds')) || 900;
  const locations = useMemo(() => s.me?.locations ?? [], [s.me?.locations]);
  const [locationId, setLocationState] = useState<string>(() => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(LOCATION_KEY);
    } catch {
      /* no storage */
    }
    const ids = (s.me?.locations ?? []).map((l) => l.id);
    if (stored && ids.includes(stored)) return stored;
    return s.me?.active_location_id && ids.includes(s.me.active_location_id)
      ? s.me.active_location_id
      : (ids[0] ?? '');
  });
  const setLocationId = useCallback((id: string) => {
    setLocationState(id);
    try {
      localStorage.setItem(LOCATION_KEY, id);
    } catch {
      /* no storage */
    }
  }, []);

  const [locked, setLocked] = useState(false);
  const [asking, setAsking] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  useIdleLock(reauthSeconds, () => setLocked(true));

  const requestReauth = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      resolver.current?.(false);
      resolver.current = resolve;
      setAsking(true);
    });
  }, []);
  const settle = useCallback((ok: boolean) => {
    setAsking(false);
    const r = resolver.current;
    resolver.current = null;
    r?.(ok);
  }, []);

  const ensureFresh = useCallback(async () => {
    if (await tokenIsFresh(reauthSeconds)) return true;
    return requestReauth();
  }, [reauthSeconds, requestReauth]);

  const invalidate = useCallback(async () => {
    await Promise.all(REVIEW_INVALIDATE.map((k) => qc.invalidateQueries({ queryKey: k })));
  }, [qc]);

  const run = useCallback(
    async <K extends CommandKind>(kind: K, payload: CommandPayloads[K]) => {
      const commandId = newCommandId();
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const r = await runCommand(kind, payload, { commandId, locationId });
          await invalidate();
          return r;
        } catch (e) {
          const err = toAppError(e);
          if (err.code === 'reauth_required' && attempt === 0) {
            if (await requestReauth()) continue;
            return null;
          }
          toast(err.message, 'danger');
          if (err.code === 'conflict') await invalidate();
          return null;
        }
      }
      return null;
    },
    [invalidate, locationId, requestReauth, toast],
  );

  const value = useMemo<OwnerReviewContext>(
    () => ({ locationId, setLocationId, run, ensureFresh }),
    [locationId, setLocationId, run, ensureFresh],
  );

  if (!locationId) return <NoAccess reason="No location to review yet. Create a location first." />;

  return (
    <Ctx.Provider value={value}>
      {locations.length > 1 && (
        <div className="d-page rv-location">
          <Field label="Location">
            {(id) => (
              <Select
                id={id}
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                data-testid="review-location"
              >
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      )}
      {locked ? (
        <div className="d-page">
          <Surface>
            <div className="d-gate">
              <h2>Locked after inactivity</h2>
              <p className="d-muted" data-testid="review-locked">
                Verify it is you to see the figures again.
              </p>
            </div>
          </Surface>
        </div>
      ) : (
        children
      )}
      <ReauthSheet
        open={locked || asking}
        reason={locked ? 'locked' : 'action'}
        onVerified={() => {
          setLocked(false);
          settle(true);
        }}
        onCancel={() => {
          if (locked) {
            setLocked(false);
            settle(false);
            nav('/', { replace: true });
          } else settle(false);
        }}
      />
    </Ctx.Provider>
  );
}

/** The authenticator code (or the password, without an authenticator): a new token with a fresh `amr`. */
function ReauthSheet({
  open,
  reason,
  onVerified,
  onCancel,
}: {
  open: boolean;
  reason: 'locked' | 'action';
  onVerified: () => void;
  onCancel: () => void;
}) {
  return (
    <Sheet
      open={open}
      onClose={onCancel}
      title={reason === 'locked' ? 'Verify it is you' : 'Confirm it is you'}
    >
      <ReauthForm reason={reason} onVerified={onVerified} onCancel={onCancel} />
    </Sheet>
  );
}

/** Mounted only while the sheet is open, so every opening starts clean. Also used inside another
 * sheet (the money given back on the POS), with a note saying what asks for it. */
export function ReauthForm({
  reason,
  note,
  onVerified,
  onCancel,
}: {
  reason: 'locked' | 'action';
  note?: string;
  onVerified: () => void;
  onCancel: () => void;
}) {
  const s = useSession();
  const online = useOnline();
  const mfa = s.me?.mfa_enrolled === true;
  const [factorId, setFactorId] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!mfa) return;
    let alive = true;
    supabase.auth.mfa.listFactors().then(({ data }) => {
      if (!alive) return;
      const f = data?.totp.find((x) => x.status === 'verified');
      setFactorId(f?.id ?? null);
    });
    return () => {
      alive = false;
    };
  }, [mfa]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mfa) {
        if (!factorId) throw new Error('no factor');
        const { error: err } = await supabase.auth.mfa.challengeAndVerify({
          factorId,
          code: value.trim(),
        });
        if (err) throw err;
      } else {
        const { data } = await supabase.auth.getSession();
        const email = data.session?.user.email;
        if (!email) throw new Error('no email');
        const { error: err } = await supabase.auth.signInWithPassword({ email, password: value });
        if (err) throw err;
      }
      setBusy(false);
      onVerified();
    } catch (thrown) {
      setBusy(false);
      // a dropped connection is not a wrong code or password
      setError(
        isNetworkError(thrown)
          ? errorText(thrown)
          : mfa
            ? 'That code was not accepted. Codes rotate every 30 seconds.'
            : 'That password was not accepted.',
      );
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="d-stack" data-testid="reauth-sheet">
      <p className="d-muted">
        {reason === 'locked'
          ? 'The review pages lock after a while without activity.'
          : (note ??
            'Clearing an item or marking something reviewed asks for a fresh verification.')}
      </p>
      <Field
        label={mfa ? '6-digit code' : 'Password'}
        help={mfa ? 'From your authenticator app' : 'The password of this login'}
      >
        {(id) =>
          mfa ? (
            <Input
              id={id}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="reauth-code"
              autoFocus
            />
          ) : (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="reauth-password"
              autoFocus
            />
          )
        }
      </Field>
      {error && (
        <div className="d-error" role="alert" data-testid="reauth-error">
          {error}
        </div>
      )}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        block
        busy={busy}
        disabled={!online || (mfa ? !factorId || value.length < 6 : value.length === 0)}
        data-testid="reauth-submit"
      >
        Verify
      </Button>
      <Button variant="quiet" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}
